import React, { ReactElement } from 'react';
import {
  ApiVersion,
  ProjectType,
  SmsTask,
  TeamPermission,
  useProjectTasksService
} from 'nekst-api';
import {
  AfterSubmitFunc,
  CheckboxInput,
  FormTemplate, IfFormData,
  RelativeRow,
  Right,
  TextInput
} from 'ui-builder';
import { ProjectParentTaskRelationInput, SendSmsButton } from 'features/tasks-forms';

import ProjectDropdown from '../ProjectDropdown';
import Row3x3x6x from '../../../../../shared/web/uitheme/form/Row3x3x6x';
import SendDate from '../Email/SendDate';
import Row8x4x from '../../../../../shared/web/uitheme/form/Row8x4x';
import Separator from '../../../../../shared/web/uitheme/form/Separator';
import TaskCommentsListInput from '../comments/TaskCommentsListInput';
import SmsTaskAssignmentsInput from './SmsTaskAssignmentInput';
import OverflowHidden from '../../../../../shared/widgets/OverflowHidden';
import SmsSmartTagsWysiwyg from '../../../../../shared/widgets/SmartTags/SmsSmartTagsWysiwyg';
import {
  ProjectSmsSmartTagsContext
} from '../../../../../shared/smarttags/SmsSmartTagsContext';
import { IfWeekendDropdown, ProjectIdContext } from 'features/nekst-widgets';
import { useAuthorizationChecker } from 'authorization-scope';
import useFeatureToggleService, { ToggledFeature } from 'feature-toggle';
import { EmptyCell } from '../../../../../shared/web/uitheme/form/RelativeRow';

export interface BaseSmsTaskFormProps {
  submitFormFunc: (data: SmsTask) => Promise<SmsTask>;
  afterSubmitFunc?: AfterSubmitFunc,
  title: string | ReactElement;
  buttons: ReactElement
  initialData?: SmsTask,
  isDisabled?: boolean | ((data: SmsTask) => boolean)
  showProjectDropdown?: boolean,
  children?: ReactElement,
  // eslint-disable-next-line react/no-unused-prop-types
  projectType?: ProjectType
}

function CheckboxesRow(props: {
  initialData?: SmsTask
}) {
  const { isGranted } = useAuthorizationChecker();

  if (isGranted(TeamPermission.FEATURE_BUSINESS_DAYS) && props.initialData?.parentTaskRelation) {
    return (
      <RelativeRow weights={[2.5, 2.5, 3, 4]}>
        <CheckboxInput source="isKeyDate" label="Key Date" />
        <CheckboxInput source="isPrivate" label="Private" />
        <CheckboxInput source="useBusinessDaysCalculation" label="Business Days" />
        <CheckboxInput source="isAutoSent" label="Autosend" />
      </RelativeRow>
    );
  } else {
    return (
      <Row3x3x6x>
        <CheckboxInput source="isKeyDate" label="Key Date" />
        <CheckboxInput source="isPrivate" label="Private" />
        <CheckboxInput source="isAutoSent" label="Autosend" />
      </Row3x3x6x>
    );
  }
}

function DependentTaskDueDateRow() {
  const featureToggleService = useFeatureToggleService();

  if (featureToggleService.isFeatureEnabled(ToggledFeature.BUSINESS_DAYS)) {
    return (
      <IfFormData conditionFunc={(data) => !!data.parentTaskRelation}>
        <RelativeRow
          weights={[8, 4]}
          useFlex
          toBottom
        >
          <ProjectParentTaskRelationInput source="parentTaskRelation" label="Send" />
          <RelativeRow weights={[1, 11]}>
            <EmptyCell />
            <IfWeekendDropdown />
          </RelativeRow>
        </RelativeRow>
      </IfFormData>
    );
  } else {
    return (
      <IfFormData conditionFunc={(data) => !!data.parentTaskRelation}>
        <Row8x4x>
          <ProjectParentTaskRelationInput source="parentTaskRelation" label="Send" />
        </Row8x4x>
      </IfFormData>
    );

  }
}

function SmsTaskForm(props: BaseSmsTaskFormProps) {
  const projectTasksService = useProjectTasksService();

  const isDisabled = props.isDisabled ?? false;

  const showProjectDropdown = props.showProjectDropdown || false;

  const featureToggleService = useFeatureToggleService();

  return (
    <ProjectIdContext projectId={props.initialData?.projectId || 0}>
      <FormTemplate<SmsTask>
        title={props.title}
        submitFormFunc={props.submitFormFunc}
        afterSubmitFunc={props.afterSubmitFunc}
        buttons={props.buttons}
        isDisabled={isDisabled}
        initialData={{
          ...props.initialData || {},
          _version: featureToggleService.isFeatureEnabled(ToggledFeature.BUSINESS_DAYS) ? ApiVersion.IF_WEEKEND_SHIFT : undefined
        }}
        validationSchema={projectTasksService.getSmsTaskValidationSchema()}
      >
        {showProjectDropdown && (<ProjectDropdown source="projectId" />)}
        <TextInput source="name" label="Task Name" />
        <CheckboxesRow initialData={props.initialData} />
        <RelativeRow
          weights={[7, 5]}
          isVisible={(data) => !data.parentTaskRelation}
        >
          <RelativeRow weights={[5, 7]}>
            <SendDate />
          </RelativeRow>
        </RelativeRow>
        <DependentTaskDueDateRow />
        <Separator />
        <SmsSmartTagsWysiwyg source="description" label="SMS message" />
        <OverflowHidden>
          <Right isVisible={(data) => data.id}>
            <SendSmsButton addPreviewPrefix />
          </Right>
        </OverflowHidden>
        <Separator />
        <SmsTaskAssignmentsInput />
        <Separator isVisible={(data) => data.id} />
        <TaskCommentsListInput isVisible={(data) => data.id} />
        {props.children || null}
      </FormTemplate>
    </ProjectIdContext>
  );
}

export default function BaseSmsTaskForm(props: BaseSmsTaskFormProps) {
    return (
      <ProjectSmsSmartTagsContext
        projectId={props.initialData?.projectId}
      >
        <SmsTaskForm {...props} />
      </ProjectSmsSmartTagsContext>
    );
}
